import { useEffect, useState } from "react";
import { Card, Group, Loader, Select, SimpleGrid, Stack, Table, Text, Title } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { api } from "../../api/client";
import { ResponsiveTable } from "../responsiveTable/ResponsiveTable";
import type {
  Customer, CustomersStatsResponse, CustomerStatDetail, CustomerStatTotals, CustomerPlanCell,
} from "../../api/types";

function Kpi({ label, value, accent }: { label: string; value: number; accent?: string }) {
  return (
    <Card withBorder padding="sm">
      <Text size="xs" c="dimmed">{label}</Text>
      <Text fw={700} size="xl" c={accent}>{value}</Text>
    </Card>
  );
}

function PlanCard({ title, cell, t }: { title: string; cell: CustomerPlanCell; t: (k: string) => string }) {
  return (
    <Card withBorder padding="sm">
      <Text fw={600} size="sm" mb={4}>{title}</Text>
      <Group gap="lg">
        <Text size="sm">{t("shipmentStats.customerStats.planned")}: <b>{cell.planned}</b></Text>
        <Text size="sm">{t("shipmentStats.customerStats.deliveredShort")}: <b>{cell.delivered}</b></Text>
        <Text size="sm" c={cell.remaining > 0 ? "orange" : "green"}>
          {t("shipmentStats.customerStats.remaining")}: <b>{cell.remaining}</b>
        </Text>
      </Group>
    </Card>
  );
}

export function CustomerStatsTab() {
  const { t } = useTranslation();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [all, setAll] = useState<CustomersStatsResponse | null>(null);
  const [detail, setDetail] = useState<CustomerStatDetail | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.listCustomers().then((r) => setCustomers(r.filter((c) => c.is_active !== false)));
  }, []);

  useEffect(() => {
    setLoading(true);
    if (!selected) {
      setDetail(null);
      api.customersStats().then(setAll).finally(() => setLoading(false));
    } else {
      setAll(null);
      api.customerStatDetail(Number(selected)).then(setDetail).finally(() => setLoading(false));
    }
  }, [selected]);

  const K = (k: string) => t(`shipmentStats.customerStats.${k}`);

  function kpis(tot: CustomerStatTotals) {
    return (
      <SimpleGrid cols={{ base: 2, sm: 4 }}>
        <Kpi label={K("delivered")} value={tot.delivered} />
        <Kpi label={K("atClient")} value={tot.at_client} accent="teal" />
        <Kpi label={K("returned")} value={tot.returned} accent="orange" />
        <Kpi label={K("inReturnBox")} value={tot.in_return_box} />
        <Kpi label={K("defect")} value={tot.defect} accent="red" />
        <Kpi label={K("restored")} value={tot.restored} />
        <Kpi label={K("onDebt")} value={tot.on_debt} accent="grape" />
        <Kpi label={K("exchange")} value={tot.exchange} />
      </SimpleGrid>
    );
  }

  return (
    <Stack>
      <Select
        label={K("selectCustomer")}
        placeholder={K("allCustomers")}
        clearable
        searchable
        data={customers.map((c) => ({ value: String(c.id), label: c.name }))}
        value={selected}
        onChange={setSelected}
        maw={360}
      />

      {loading && <Loader />}

      {!loading && !selected && all && (
        <ResponsiveTable
          forceMode="sticky"
          stickyColumns={1}
          mobile={{
            rows: all.customers,
            rowKey: (c) => c.customer_id,
            title: (c) => c.name ?? "—",
            fields: [
              { label: K("delivered"), render: (c) => c.totals.delivered },
              { label: K("atClient"), render: (c) => c.totals.at_client },
              { label: K("returned"), render: (c) => c.totals.returned },
              { label: K("defect"), render: (c) => c.totals.defect },
              { label: K("onDebt"), render: (c) => c.totals.on_debt },
              { label: K("exchange"), render: (c) => c.totals.exchange },
              { label: `${K("planMonth")} (${K("remaining")})`, render: (c) => c.plan_month.remaining },
              { label: `${K("planYear")} (${K("remaining")})`, render: (c) => c.plan_year.remaining },
            ],
            onOpen: (c) => setSelected(String(c.customer_id)),
          }}
        >
        <Table.ScrollContainer minWidth={900}>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{K("customer")}</Table.Th>
                <Table.Th>{K("delivered")}</Table.Th>
                <Table.Th>{K("atClient")}</Table.Th>
                <Table.Th>{K("returned")}</Table.Th>
                <Table.Th>{K("defect")}</Table.Th>
                <Table.Th>{K("onDebt")}</Table.Th>
                <Table.Th>{K("exchange")}</Table.Th>
                <Table.Th>{K("planMonth")} ({K("remaining")})</Table.Th>
                <Table.Th>{K("planYear")} ({K("remaining")})</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {all.customers.map((c) => (
                <Table.Tr key={c.customer_id}>
                  <Table.Td>
                    <Text component="span" style={{ cursor: "pointer" }}
                      onClick={() => setSelected(String(c.customer_id))}>{c.name ?? "—"}</Text>
                  </Table.Td>
                  <Table.Td>{c.totals.delivered}</Table.Td>
                  <Table.Td>{c.totals.at_client}</Table.Td>
                  <Table.Td>{c.totals.returned}</Table.Td>
                  <Table.Td>{c.totals.defect}</Table.Td>
                  <Table.Td>{c.totals.on_debt}</Table.Td>
                  <Table.Td>{c.totals.exchange}</Table.Td>
                  <Table.Td>{c.plan_month.remaining}</Table.Td>
                  <Table.Td>{c.plan_year.remaining}</Table.Td>
                </Table.Tr>
              ))}
              <Table.Tr>
                <Table.Td><b>{K("total")}</b></Table.Td>
                <Table.Td><b>{all.total.totals.delivered}</b></Table.Td>
                <Table.Td><b>{all.total.totals.at_client}</b></Table.Td>
                <Table.Td><b>{all.total.totals.returned}</b></Table.Td>
                <Table.Td><b>{all.total.totals.defect}</b></Table.Td>
                <Table.Td><b>{all.total.totals.on_debt}</b></Table.Td>
                <Table.Td><b>{all.total.totals.exchange}</b></Table.Td>
                <Table.Td><b>{all.total.plan_month.remaining}</b></Table.Td>
                <Table.Td><b>{all.total.plan_year.remaining}</b></Table.Td>
              </Table.Tr>
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
        </ResponsiveTable>
      )}

      {!loading && selected && detail && (
        <Stack>
          <Title order={4}>{detail.customer.name}</Title>
          {kpis(detail.totals)}
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <PlanCard title={K("planMonth")} cell={detail.plan_month} t={t} />
            <PlanCard title={K("planYear")} cell={detail.plan_year} t={t} />
          </SimpleGrid>

          <Title order={5}>{K("shipments")}</Title>
          <ResponsiveTable
            mobile={{
              rows: detail.shipments,
              rowKey: (s) => s.id,
              title: (s) => <Link to={`/shipments/${s.id}`}>{s.code}</Link>,
              subtitle: (s) => s.shipped_date ?? undefined,
              fields: [
                { label: K("count"), render: (s) => s.device_count },
                { label: K("type"), render: (s) => s.delivery_type_name ?? "—" },
                { label: K("onDebt"), render: () => "✓", hidden: (s) => !s.on_debt },
              ],
            }}
          >
          <Table.ScrollContainer minWidth={600}>
            <Table striped>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{K("code")}</Table.Th><Table.Th>{K("date")}</Table.Th>
                  <Table.Th>{K("count")}</Table.Th><Table.Th>{K("type")}</Table.Th><Table.Th>{K("onDebt")}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {detail.shipments.map((s) => (
                  <Table.Tr key={s.id}>
                    <Table.Td><Link to={`/shipments/${s.id}`}>{s.code}</Link></Table.Td>
                    <Table.Td>{s.shipped_date ?? "—"}</Table.Td>
                    <Table.Td>{s.device_count}</Table.Td>
                    <Table.Td>{s.delivery_type_name ?? "—"}</Table.Td>
                    <Table.Td>{s.on_debt ? "✓" : ""}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
          </ResponsiveTable>

          <Title order={5}>{K("monthly")}</Title>
          <ResponsiveTable
            forceMode="sticky"
            stickyColumns={1}
            mobile={{
              rows: detail.monthly,
              rowKey: (m) => m.period_month,
              title: (m) => m.period_month,
              fields: [
                { label: K("planned"), render: (m) => m.planned },
                { label: K("deliveredShort"), render: (m) => m.billed },
                { label: K("remaining"), render: (m) => m.remaining },
              ],
            }}
          >
          <Table striped maw={520}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{K("month")}</Table.Th><Table.Th>{K("planned")}</Table.Th>
                <Table.Th>{K("deliveredShort")}</Table.Th><Table.Th>{K("remaining")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {detail.monthly.map((m) => (
                <Table.Tr key={m.period_month}>
                  <Table.Td>{m.period_month}</Table.Td>
                  <Table.Td>{m.planned}</Table.Td>
                  <Table.Td>{m.billed}</Table.Td>
                  <Table.Td>{m.remaining}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
          </ResponsiveTable>
        </Stack>
      )}
    </Stack>
  );
}
